import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { User, Subscription, PaymentRow } from '../types';
import toast from 'react-hot-toast';

const formatRub = (kopecks: number): string =>
  (kopecks / 100).toLocaleString('ru-RU', { maximumFractionDigits: 0 }) + ' ₽';

const formatDate = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('ru-RU');
};

const PAYMENT_STATUS: Record<string, { label: string; cls: string }> = {
  confirmed: { label: 'Оплачен', cls: 'bg-green-100 text-green-800' },
  authorized: { label: 'Оплачен', cls: 'bg-green-100 text-green-800' },
  new: { label: 'Ожидает оплаты', cls: 'bg-amber-100 text-amber-800' },
  rejected: { label: 'Отклонён', cls: 'bg-red-100 text-red-800' },
  refunded: { label: 'Возврат', cls: 'bg-gray-100 text-gray-700' },
  canceled: { label: 'Отменён', cls: 'bg-gray-100 text-gray-700' },
};

const Account: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState<User | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [payments, setPayments] = useState<PaymentRow[]>([]);

  // Профиль
  const [fullName, setFullName] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  // Пароль
  const [curPwd, setCurPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [savingPwd, setSavingPwd] = useState(false);

  // Промокод
  const [promo, setPromo] = useState('');
  const [redeeming, setRedeeming] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [meData, sub, pays] = await Promise.all([
        apiService.getMe(),
        apiService.getSubscription().catch(() => null),
        apiService.getMyPayments().catch(() => []),
      ]);
      setMe(meData);
      setFullName(meData.full_name || '');
      setSubscription(sub);
      setPayments(pays);
    } catch {
      toast.error('Не удалось загрузить кабинет');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!apiService.isAuthenticated()) {
      navigate('/login');
      return;
    }
    load();
  }, [navigate, load]);

  const saveProfile = async () => {
    try {
      setSavingProfile(true);
      const updated = await apiService.updateProfile({ full_name: fullName });
      setMe(updated);
      toast.success('Профиль сохранён');
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Не удалось сохранить профиль');
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async () => {
    if (!curPwd || !newPwd) {
      toast.error('Заполните оба поля пароля');
      return;
    }
    try {
      setSavingPwd(true);
      await apiService.changePassword(curPwd, newPwd);
      setCurPwd('');
      setNewPwd('');
      toast.success('Пароль изменён');
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Не удалось сменить пароль');
    } finally {
      setSavingPwd(false);
    }
  };

  const handleRedeem = async () => {
    const code = promo.trim();
    if (!code) return;
    try {
      setRedeeming(true);
      const r = await apiService.redeemPromo(code);
      setSubscription(r.subscription);
      setPromo('');
      toast.success(r.message || 'Промокод активирован');
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Не удалось активировать промокод');
    } finally {
      setRedeeming(false);
    }
  };

  const sub = subscription;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Шапка */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center py-4">
          <h1 className="text-xl font-bold text-gray-900">Личный кабинет</h1>
          <button
            onClick={() => navigate('/dashboard')}
            className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
          >
            ← К заявкам
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {loading ? (
          <div className="text-gray-400">Загрузка…</div>
        ) : (
          <>
            {/* Подписка */}
            <section className="rounded-lg border border-gray-200 bg-white p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                  Подписка
                </h2>
                <button
                  onClick={() => navigate('/billing')}
                  className="text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md px-3 py-1.5"
                >
                  {sub && sub.active ? 'Продлить / сменить тариф' : 'Выбрать тариф'}
                </button>
              </div>
              {sub && sub.active ? (
                <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
                  <div>
                    <div className="text-gray-400">Тариф</div>
                    <div className="font-medium text-gray-900">{sub.plan_name || '—'}</div>
                  </div>
                  <div>
                    <div className="text-gray-400">Действует до</div>
                    <div className="font-medium text-gray-900">{formatDate(sub.expires_at)}</div>
                  </div>
                  <div>
                    <div className="text-gray-400">Заявок использовано</div>
                    <div className="font-medium text-gray-900">
                      {sub.request_quota == null
                        ? `${sub.requests_used} (без лимита)`
                        : `${sub.requests_used} из ${sub.request_quota}`}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-600">
                  Активной подписки нет. Выберите тариф или введите промокод, чтобы получить доступ.
                </p>
              )}

              {/* Промокод */}
              <div className="mt-4 pt-4 border-t border-gray-100">
                <label className="block text-xs text-gray-500 mb-1">Есть промокод?</label>
                <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                  <input
                    value={promo}
                    onChange={(e) => setPromo(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleRedeem(); }}
                    placeholder="Введите промокод"
                    className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm uppercase"
                  />
                  <button
                    onClick={handleRedeem}
                    disabled={redeeming || !promo.trim()}
                    className="text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-md px-4 py-2 disabled:opacity-50"
                  >
                    {redeeming ? 'Активация…' : 'Активировать промокод'}
                  </button>
                </div>
              </div>
            </section>

            {/* Профиль */}
            <section className="rounded-lg border border-gray-200 bg-white p-5">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
                Профиль
              </h2>
              {/* Отдельная форма профиля: браузер не должен путать её с формой логина */}
              <form onSubmit={(e) => { e.preventDefault(); saveProfile(); }} autoComplete="off">
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Email</label>
                    <input
                      type="email"
                      value={me?.email || ''}
                      disabled
                      className="w-full border border-gray-200 bg-gray-50 rounded-md px-3 py-2 text-sm text-gray-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">ФИО</label>
                    <input
                      type="text"
                      name="full_name"
                      autoComplete="name"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder="Иванов Иван Иванович"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                </div>
                <p className="mt-2 text-xs text-gray-400">
                  ФИО используется для документов при оплате. Чек придёт на ваш email.
                </p>
                <button
                  type="submit"
                  disabled={savingProfile}
                  className="mt-4 text-sm font-medium text-white bg-gray-800 hover:bg-gray-900 rounded-md px-4 py-2 disabled:opacity-50"
                >
                  {savingProfile ? 'Сохранение…' : 'Сохранить профиль'}
                </button>
              </form>

              {/* Смена пароля — своя форма, чтобы автозаполнение не лезло в профиль */}
              <form
                onSubmit={(e) => { e.preventDefault(); savePassword(); }}
                className="mt-6 pt-6 border-t border-gray-100"
              >
                <h3 className="text-sm font-medium text-gray-700 mb-3">Смена пароля</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Текущий пароль</label>
                    <input
                      type="password"
                      value={curPwd}
                      onChange={(e) => setCurPwd(e.target.value)}
                      autoComplete="current-password"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Новый пароль</label>
                    <input
                      type="password"
                      value={newPwd}
                      onChange={(e) => setNewPwd(e.target.value)}
                      autoComplete="new-password"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={savingPwd}
                  className="mt-4 text-sm font-medium text-gray-700 border border-gray-300 hover:bg-gray-50 rounded-md px-4 py-2 disabled:opacity-50"
                >
                  {savingPwd ? 'Сохранение…' : 'Изменить пароль'}
                </button>
              </form>
            </section>

            {/* Платежи */}
            <section className="rounded-lg border border-gray-200 bg-white p-5">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Платежи и документы
              </h2>
              {payments.length === 0 ? (
                <p className="text-sm text-gray-500">Платежей пока нет.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="text-gray-500">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">Дата</th>
                        <th className="px-3 py-2 text-left font-medium">Тариф</th>
                        <th className="px-3 py-2 text-left font-medium">Сумма</th>
                        <th className="px-3 py-2 text-left font-medium">Статус</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {payments.map((p) => {
                        const st = PAYMENT_STATUS[p.status] || { label: p.status, cls: 'bg-gray-100 text-gray-700' };
                        return (
                          <tr key={p.id}>
                            <td className="px-3 py-2">{formatDate(p.created_at)}</td>
                            <td className="px-3 py-2">{p.plan_name || '—'}</td>
                            <td className="px-3 py-2">{formatRub(p.amount_kopecks)}</td>
                            <td className="px-3 py-2">
                              <span className={`inline-block px-2 py-0.5 rounded ${st.cls}`}>
                                {st.label}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default Account;
